import { getAccessToken, getScopeHeaders } from '../lib/auth-storage';

const API_BASE = import.meta.env.VITE_AUTHZ_API ?? '/api/v1';

export type Permission = {
  key: string;
  resource: string;
  action: string;
  description: string;
  group?: string;
};

export type Role = {
  id: string;
  name: string;
  description?: string;
  parent_role_id?: string;
  permissions?: string[];
};

export type Policy = {
  id: string;
  name: string;
  description?: string;
  effect: 'allow' | 'deny';
  priority: number;
  resource: string;
  action: string;
  tenant_id?: string;
  workspace_id?: string;
  org_id?: string;
  enabled: boolean;
  conditions?: { field: string; operator: string; value: string }[];
};

export type CheckResult = {
  allowed: boolean;
  reason?: string;
  matched_via?: string[];
};

function headers(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...getScopeHeaders(),
    ...(getAccessToken() ? { Authorization: `Bearer ${getAccessToken()}` } : {}),
  };
}

async function authzFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers: { ...headers(), ...(init?.headers as Record<string, string> | undefined) } });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Authz ${path} failed: ${res.status} ${text}`);
  }
  const body = await res.json();
  return (body.data ?? body) as T;
}

export const authzApi = {
  listRoles: () => authzFetch<Role[]>('/roles'),
  createRole: (role: Partial<Role>) => authzFetch<Role>('/roles', { method: 'POST', body: JSON.stringify(role) }),
  updateRole: (id: string, role: Partial<Role>) => authzFetch<Role>(`/roles/${id}`, { method: 'PUT', body: JSON.stringify(role) }),
  deleteRole: (id: string) => authzFetch<{ message: string }>(`/roles/${id}`, { method: 'DELETE' }),
  setRolePermissions: (id: string, permissions: string[]) =>
    authzFetch<{ message: string }>(`/roles/${id}/permissions`, { method: 'POST', body: JSON.stringify({ permissions }) }),

  listPermissions: () => authzFetch<Permission[]>('/permissions'),
  createPermission: (p: Partial<Permission>) => authzFetch<Permission>('/permissions', { method: 'POST', body: JSON.stringify(p) }),
  deletePermission: (key: string) => authzFetch<{ message: string }>(`/permissions/${encodeURIComponent(key)}`, { method: 'DELETE' }),

  assignRole: (userId: string, roleId: string) =>
    authzFetch<{ message: string }>(`/users/${userId}/roles`, { method: 'POST', body: JSON.stringify({ role_id: roleId }) }),
  revokeRole: (userId: string, roleId: string) =>
    authzFetch<{ message: string }>(`/users/${userId}/roles/${roleId}`, { method: 'DELETE' }),

  listPolicies: () => authzFetch<Policy[]>('/policies'),
  createPolicy: (p: Partial<Policy>) => authzFetch<Policy>('/policies', { method: 'POST', body: JSON.stringify(p) }),
  updatePolicy: (id: string, p: Partial<Policy>) => authzFetch<Policy>(`/policies/${id}`, { method: 'PUT', body: JSON.stringify(p) }),
  deletePolicy: (id: string) => authzFetch<{ message: string }>(`/policies/${id}`, { method: 'DELETE' }),

  check: (userId: string, resource: string, action: string, scope?: Record<string, string>) =>
    authzFetch<CheckResult>('/authorization/check', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, resource, action, scope }),
    }),
  myPermissions: () => authzFetch<{ permissions: string[]; user_id: string }>('/authorization/me'),
  listAudit: (limit = 50) => authzFetch<unknown[]>(`/authorization/audit?limit=${limit}`),
};
